import { AppCollector, AppValue } from 'lib/actions/apps'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { CreateReturn, ListReturn, Status } from 'lib/rpc'
import { ActionStatus, Collector, unaryInvoke, unaryListCollect } from 'lib/rpcts'
import { AccessReviewConnectorSource } from 'pbts/c1/api/accessreview/v1/accessreview'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    Connector, ConnectorServiceCreateDelegatedRequest, ConnectorServiceCreateResponse, ConnectorSyncLifecycle,
} from 'pbts/c1/api/app/v1/connector'
import { ConnectorServiceClient } from 'pbts/c1/api/app/v1/connector.client'
import {
    ConfigSchema, ConnectorCatalog, ConnectorCatalogServiceConfigurationSchemaRequest, ConnectorCatalogServiceConfigurationSchemaResponse,
    ConnectorCatalogServiceGetRequest, ConnectorCatalogServiceGetResponse, ConnectorCatalogServiceSearchAppsForInstallationRequest,
    ConnectorCatalogServiceSearchAppsForInstallationResponse, ConnectorCatalogServiceSearchRequest, ConnectorCatalogServiceSearchResponse,
    ConnectorCatalogView, ConnectorInstallation,
} from 'pbts/c1/api/integration/connector/v1/catalog'
import { ConnectorCatalogServiceClient } from 'pbts/c1/api/integration/connector/v1/catalog.client'
import { Any } from 'pbts/google/protobuf/any'
import { Dispatch } from 'react'

export enum ConnectorCatalogAction {
    search = 'ConnectorCatalogAction.search',
    get = 'ConnectorCatalogAction.get',
    create = 'ConnectorCatalogAction.create',
    getConfigSchema = 'ConnectorCatalogAction.getConfigSchema',
    searchAppsForInstallation = 'ConnectorCatalogAction.searchAppsForInstallation',
}

export type SearchConnectorCatalogAction = {
    type: ConnectorCatalogAction.search
    payload: ListReturn<ConnectorCatalogValue>
}

export type GetConnectorCatalogAction = {
    type: ConnectorCatalogAction.get
    payload: ActionStatus<ConnectorCatalogValue>
}

export type SearchAppsForInstallationCatalogAction = {
    type: ConnectorCatalogAction.searchAppsForInstallation
    payload: ListReturn<AppValue>
}

export type CreateConnectorCatalogAction = {
    type: ConnectorCatalogAction.create
    payload: CreateReturn<Connector>
}

export type GetConfigSchemaCatalogAction = {
    type: ConnectorCatalogAction.getConfigSchema
    payload: ActionStatus<ConfigSchema>
}

export type ConnectorCatalogActions =
    | SearchConnectorCatalogAction
    | CreateConnectorCatalogAction
    | GetConfigSchemaCatalogAction
    | GetConnectorCatalogAction
    | SearchAppsForInstallationCatalogAction

export interface ConnectorCatalogValue extends ConnectorCatalog {
}

export const expandConnectorCatalogView = (
    msg: ConnectorCatalogView | undefined,
    _values: Any[],
): ConnectorCatalogValue => {
    return {
        ...ConnectorCatalog.create(msg?.connectorCatalog ?? {}),
    }
}

export class ConnectorCatalogCollector extends Collector<ConnectorCatalogValue> {
    push(resp: ConnectorCatalogServiceSearchResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): ConnectorCatalogValue => {
                return expandConnectorCatalogView(v, resp.expanded)
            }),
        )
    }
}

export const searchConnectorCatalog = (
    retired: boolean,
    dispatch: Dispatch<SearchConnectorCatalogAction>,
    client?: ConnectorCatalogServiceClient,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const collector = new ConnectorCatalogCollector()

    return unaryListCollect<ConnectorCatalogServiceSearchRequest, ConnectorCatalogServiceSearchResponse>(
        ConnectorCatalogServiceSearchRequest.create({ retired, expandMask: { paths: ['*'] } }),
        client.search.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: ConnectorCatalogAction.search,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                        },
                    })
                    break
                default:
                    dispatch({
                        type: ConnectorCatalogAction.search,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const getConnectorCatalog = (
    dispatch: Dispatch<GetConnectorCatalogAction>,
    client: ConnectorCatalogServiceClient | undefined,
    catalogID: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = ConnectorCatalogServiceGetRequest.create({
        catalogId: catalogID,
    })
    return unaryInvoke<ConnectorCatalogServiceGetRequest, ConnectorCatalogServiceGetResponse>(
        req,
        client.get.bind(client),
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: ConnectorCatalogAction.get,
                        payload: {
                            status: Status.OK,
                            item: expandConnectorCatalogView(
                                payload.item.catalogView || ConnectorCatalogView.create({}),
                                [],
                            ),
                        },
                    })
                    break
                default:
                    dispatch({
                        type: ConnectorCatalogAction.get,
                        payload,
                    })
                    break
            }
        },
    )
}

export interface ConnectorInstallationValue extends ConnectorInstallation {
    id: string
    connector?: Connector
    app?: App
    connectorSyncLifecycle?: ConnectorSyncLifecycle
    configType?: string
    accessReviewConnectorSource?: AccessReviewConnectorSource
    hasAnyCompletedIncrementalSync?: boolean
    lambdaDeploymentVersion?: string
    lambdaDeploymentChannel?: string
}

export const searchAppsForInstallation = (
    catalogId: string,
    dispatch: Dispatch<SearchAppsForInstallationCatalogAction>,
    client?: ConnectorCatalogServiceClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const collector = new AppCollector()

    return unaryListCollect<
        ConnectorCatalogServiceSearchAppsForInstallationRequest,
        ConnectorCatalogServiceSearchAppsForInstallationResponse
    >(
        ConnectorCatalogServiceSearchAppsForInstallationRequest.create({ catalogId }),
        client.searchAppsForInstallation.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: ConnectorCatalogAction.searchAppsForInstallation,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                        },
                    })
                    break
                default:
                    dispatch({
                        type: ConnectorCatalogAction.searchAppsForInstallation,
                        payload,
                    })
            }
        },
    )
}

const transformCreatePayload = (v: ActionStatus<ConnectorServiceCreateResponse>): ActionStatus<Connector> => {
    switch (v.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: Connector.create(v.item.connectorView?.connector ?? {}),
            }
        default:
            return v
    }
}

export const createConnector = (
    dispatch: Dispatch<CreateConnectorCatalogAction>,
    connectorClient: ConnectorServiceClient | undefined,
    displayName: string,
    description: string,
    catalogId: string,
    appId?: string,
    userIds?: string[],
): CancelFunc => {
    if (!connectorClient) {
        return emptyCancel
    }

    const connectorReq = ConnectorServiceCreateDelegatedRequest.create({
        displayName,
        description,
        catalogId,
        appId,
        userIds,
        expandMask: { paths: ['*'] },
    })

    return unaryInvoke<ConnectorServiceCreateDelegatedRequest, ConnectorServiceCreateResponse>(
        connectorReq,
        connectorClient.createDelegated.bind(connectorClient),
        (payload) => {
            dispatch({ type: ConnectorCatalogAction.create, payload: transformCreatePayload(payload) })
        },
    )
}

export const getConfigSchema = (
    dispatch: Dispatch<GetConfigSchemaCatalogAction>,
    client: ConnectorCatalogServiceClient | undefined,
    catalogID: string,
    appID: string,
    connectorID: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = ConnectorCatalogServiceConfigurationSchemaRequest.create({
        catalogId: catalogID,
        appId: appID,
        connectorId: connectorID,
    })
    return unaryInvoke<
        ConnectorCatalogServiceConfigurationSchemaRequest,
        ConnectorCatalogServiceConfigurationSchemaResponse
    >(req, client.configurationSchema.bind(client), (payload) => {
        switch (payload.status) {
            case Status.OK:
                dispatch({
                    type: ConnectorCatalogAction.getConfigSchema,
                    payload: { status: Status.OK, item: payload.item.schema || ConfigSchema.create({}) },
                })
                break
            default:
                dispatch({
                    type: ConnectorCatalogAction.getConfigSchema,
                    payload,
                })
                break
        }
    })
}
